import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';

/**
 * 레포 검색창 25개의 **다수파 수치**. 정본은 `Approval/RelatedApproval/styles.tsx` 의
 * `SearchBarRoot`/`SearchInputField` 이고, 이 값들이 거기와 일치한다.
 *
 * radius 는 레포가 `8px` 7건 vs `6px` 6건으로 갈려 있었는데 **8 로 정했다** —
 * `BoxButton`(8), `DatePicker/styles.ts` 의 `DateSelectorNormalizedStyle`(8),
 * 그리고 위 정본(8)과 맞춘 결과다.
 */
export const SEARCH_INPUT_HEIGHT = 44;
export const SEARCH_INPUT_RADIUS = '8px';
export const SEARCH_BORDER_IDLE = '#E7E9EC';
export const SEARCH_BORDER_ACTIVE = '#000';
export const SEARCH_PLACEHOLDER_COLOR = '#B7BBC2';
export const SEARCH_ICON_SIZE = 20;

/**
 * clear(⊗) 와 돋보기(🔍) 가 함께 들어가는 endAdornment 정리.
 *
 * 베이스(`BaseFormattedTextField`)는 둘을 **하나의 `InputAdornment` 안에** `[clear][돋보기]`
 * 순서로 넣는다. 시안(Figma `27235:141156`)의 `⊗ 🔍` 순서와 같아서 손댈 게 없다.
 *
 * 다만 clear 는 MUI `IconButton` 이라 기본 패딩 8 이 붙어 16px 아이콘이 32px 을 차지한다.
 * 패딩을 죽이고 `gap` 으로 간격을 만든다.
 */
const ADORNMENT_SX: SystemStyleObject<Theme> = {
  '& .MuiInputAdornment-root': {
    gap: '8px',
    '& .MuiIconButton-root': { padding: 0 },
  },
};

/**
 * 글자 ↔ 아이콘 간격을 정본의 `gap: 8` 로 맞춘다.
 *
 * 전역 테마(`theme/overrides/TextField.ts:33`)가 `&.MuiInputBase-input` 으로 사방 `padding: 12`
 * 를 박는데, `InputAdornment position="end"` 는 자체적으로 `margin-left: 8` 을 갖는다.
 * 그대로 두면 12 + 8 = 20 이 되어 정본보다 벌어진다. input 의 오른쪽 패딩만 죽이면 8 이 된다.
 *
 * 전역이 (0,2,0)(같은 엘리먼트 클래스 2개)이므로 **클래스를 하나 더 물려 (0,3,0)** 으로 이긴다.
 * `& .MuiInputBase-input` 같은 동점 셀렉터로는 못 이긴다 — `Select/styles.ts` 와 같은 함정.
 *
 * 왼쪽은 건드리지 않는다. 정본은 `root padding 12 + input padding 0` 으로 글자가 12 에서
 * 시작하는데, 여기는 `root 0 + input 12` 라 결과가 같다.
 */
const INPUT_PADDING_SX: SystemStyleObject<Theme> = {
  '& .MuiInputBase-input.MuiOutlinedInput-input': { paddingRight: 0 },
};

/** 전역 normalize 가 placeholder 를 반투명으로 만들어 `#B7BBC2` 가 더 흐리게 보이는 것을 막는다 */
const PLACEHOLDER_SX: SystemStyleObject<Theme> = {
  '& .MuiInputBase-input::placeholder': {
    color: SEARCH_PLACEHOLDER_COLOR,
    opacity: 1,
  },
};

/**
 * `variant="box"` — 사방 보더 + radius 8. PC SNB, 폼 필드용.
 *
 * 높이 44 는 전역 테마의 `&.MuiInputBase-root { height: 44px }` 에서 그냥 온다.
 * focus/hover 시 보더 `#000` 도 베이스가 이미 갖고 있다. 그래서 **radius 만 명시하면 된다** —
 * 베이스의 기본 radius 는 MUI 기본값 4px 이다.
 */
export const BOX_SEARCH_SX: SystemStyleObject<Theme> = {
  '& .MuiOutlinedInput-root.MuiInputBase-root': {
    borderRadius: SEARCH_INPUT_RADIUS,
    backgroundColor: 'var(--dk-bg, #fff)',
  },
  ...INPUT_PADDING_SX,
  ...PLACEHOLDER_SX,
  ...ADORNMENT_SX,
};

/**
 * `variant="underline"` — 사방 보더 없이 하단 선만. MO 검색 화면(스펙 F)용.
 * 시안: 값이 들어가면 그 선이 진해진다.
 *
 * ## 왜 셀렉터가 이렇게 긴가
 *
 * 베이스는 `sx={{ ...내부블록, ...호출부sx }}` 로 **얕게** 합친다. 그래서 베이스가 쓰는 키
 * (`'& .MuiOutlinedInput-root:not(.MuiInputBase-multiline)'`)를 우리가 다시 쓰면 그 블록이
 * **통째로 교체**된다. 베이스가 나중에 거기에 뭘 추가해도 underline 에서만 조용히 사라지고,
 * 그건 diff 에도 리뷰에도 안 보인다.
 *
 * 그래서 **같은 키를 재작성하지 않고 클래스를 더 물려 특이도로만 이긴다.** 필요한 선언만
 * 뒤집히고 나머지는 베이스에서 계속 상속된다.
 *
 * | 상대 | 특이도 |
 * |---|---|
 * | 베이스 focus/hover 보더 (`...root:not(...).Mui-focused .notchedOutline`) | (0,5,0) |
 * | 베이스 `paddingRight: 12` 블록 | (0,3,0) |
 * | 전역 테마 `&.MuiInputBase-input` | (0,2,0) |
 *
 * 아래 앵커는 (0,4,0), 그 안의 보더 무효화는 (0,6,0) 이라 각각을 넘는다.
 *
 * ## 밑줄을 root 에 직접 거는 이유
 * `notchedOutline` 은 `<fieldset>` + `<legend>` 라서 border 를 한 변만 남기면 노치 자국이
 * 남는다. outline 은 통째로 지우고 밑줄은 root 의 `border-bottom` 으로 그린다.
 */
export const underlineSearchSx = (
  hasValue: boolean,
): SystemStyleObject<Theme> => ({
  '& .MuiOutlinedInput-root.MuiInputBase-root.MuiInputBase-adornedEnd': {
    borderRadius: 0,
    paddingRight: 0,
    backgroundColor: 'transparent',
    // 시안의 "입력 시 하단 border 강조". 포커스 전에 값만 있는 상태도 진해져야 해서
    // `hasValue`(JS) 와 `:focus-within`(CSS) 둘 다로 건다
    borderBottom: `1px solid ${
      hasValue ? SEARCH_BORDER_ACTIVE : SEARCH_BORDER_IDLE
    }`,
    '&:focus-within': { borderBottomColor: SEARCH_BORDER_ACTIVE },

    // 사방 보더 제거. 앞의 두 줄이 (0,6,0) 이라 베이스의 (0,5,0) focus/hover 규칙을 이긴다
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '&:hover:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
  },
  // 언더라인은 컨테이너에 딱 붙어야 해서 좌우 패딩을 모두 죽인다 (box 와 다른 점)
  '& .MuiInputBase-input.MuiOutlinedInput-input': {
    paddingLeft: 0,
    paddingRight: 0,
  },
  ...PLACEHOLDER_SX,
  ...ADORNMENT_SX,
});
